import { supabase } from "@/integrations/supabase/client";
import { hasConsented } from "@/lib/cookieConsent";

function getSessionId(): string {
  let sid = sessionStorage.getItem("analytics_session_id");
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem("analytics_session_id", sid);
  }
  return sid;
}

function parseUserAgent(): { device_type: string; browser: string; os: string } {
  const ua = navigator.userAgent;

  let device_type = "desktop";
  if (/Mobi|Android/i.test(ua)) device_type = "mobile";
  else if (/Tablet|iPad/i.test(ua)) device_type = "tablet";

  let browser = "Unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Chrome/i.test(ua)) browser = "Chrome";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua)) browser = "Safari";
  else if (/MSIE|Trident/i.test(ua)) browser = "IE";

  let os = "Unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iOS|iPhone|iPad/i.test(ua)) os = "iOS";

  return { device_type, browser, os };
}

function getUtmParams(): Record<string, string | null> {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
  };
}

export async function trackPageView(path: string) {
  if (!hasConsented()) return;
  try {
    const { device_type, browser, os } = parseUserAgent();
    const utms = getUtmParams();
    await (supabase as any).from("page_views").insert({
      page_path: path,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      device_type,
      browser,
      os,
      screen_width: window.innerWidth,
      session_id: getSessionId(),
      ...(utms.utm_source && { utm_source: utms.utm_source }),
      ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
      ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
      ...(utms.utm_term && { utm_term: utms.utm_term }),
      ...(utms.utm_content && { utm_content: utms.utm_content }),
    });
  } catch {
    // Silent fail — analytics should never break the app
  }
}

export { getSessionId };

export async function trackScrollDepth(pagePath: string, maxDepth: number) {
  if (!hasConsented()) return;
  try {
    const sessionId = getSessionId();
    await (supabase as any).from("scroll_depth").upsert(
      { session_id: sessionId, page_path: pagePath, max_depth: maxDepth },
      { onConflict: "session_id,page_path" }
    );
  } catch {
    // Silent fail
  }
}

export async function trackReviewInteraction(
  reviewId: string,
  interactionType:
    | "card_click"
    | "read_more"
    | "clutch_link"
    | "case_study_click"
    | "case_study_click_card"
) {
  if (!hasConsented()) return;
  try {
    await (supabase as any).from("review_interactions").insert({
      review_id: reviewId,
      interaction_type: interactionType,
      page_path: window.location.pathname,
    });
  } catch {
    // Silent fail
  }
}
